<template>
  <div class="smart-folders mt-3">
    <SmartFoldersConfigurationModal v-if="showConfigurationModal" @close="showConfigurationModal = false" />
    <SmartFoldersMissingConfig
      v-if="!activeConfiguration?.enabledSmartFolderProperties?.length && !isFetchingCanvases && !isLoadingConfig"
      @configure="onSmartFoldersConfigure"
    />
    <div v-else>
      <div class="flex justify-between items-center mb-3">
        <div class="flex items-center gap-2">
          <CSavedCanvasesManagementOwnerCell
            v-if="isAnyTypeOfAdmin"
            dropdown-mode
            editable
            :my-user="myUser"
            :owner="selectedOwner"
            @update:owner="onOwnerChange"
          />
          <NBreadcrumb v-if="shouldDisplayTable && breadcrumbItems.length" class="overflow-x-auto" separator=">">
            <NBreadcrumbItem
              v-for="(item, index) in breadcrumbItems"
              :key="index"
              :class="{ 'cursor-pointer': index !== breadcrumbItems.length - 1 }"
              :clickable="index !== breadcrumbItems.length - 1"
              @click="isNavigatable ? onBreadcrumbClick(item) : null"
            >
              {{
                !index
                  ? isNavigatable
                    ? t('canvasUI.smartFolders.rootFolders')
                    : t('canvasUI.smartFolders.rootCanvases')
                  : item.label
              }}
            </NBreadcrumbItem>
          </NBreadcrumb>
        </div>

        <CButton
          v-if="
            shouldDisplayTable &&
            (activeConfiguration?.enabledSmartFolderProperties?.length ?? 0) >= breadcrumbItems.length
          "
          @click="toggleSmartFoldersDisplayMode"
        >
          {{
            smartFoldersDisplayMode === 'folders'
              ? t('canvasUI.smartFolders.showCanvases')
              : t('canvasUI.smartFolders.showFolders')
          }}
        </CButton>
      </div>
      <CTable
        v-if="shouldDisplayTable"
        ref="tableRef"
        :columns="columns as any"
        :data="displayItems"
        :loading="isFetchingCanvases || isLoadingConfig"
        :page-index="page - 1"
        :page-size="pageSize"
        :page-sizes="pageSizes"
        :scroller-style="{
          height: `calc(100vh - 400px - ${toolbarHeight || 0}px`,
        }"
        :total-count="totalCount"
        @page-changed="onTableChange('pageChanged', $event)"
        @page-size-changed="onTableChange('pageSizeChanged', $event)"
        @row-click="onRowClick"
      />

      <div v-else>
        <CErrorFullScreen
          class="pa-10 rounded-2"
          :description="t('canvasUI.smartFolders.selectUserDesc')"
          fit-parent
          icon="face-dotted"
          :title="t('canvasUI.smartFolders.selectUserTitle')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, inject, type ComputedRef } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { NBreadcrumb, NBreadcrumbItem } from 'naive-ui'

import type { User, CanvasRetrieve } from '@openapi'
import CSavedCanvasesManagementOwnerCell, {
  type Owner,
} from '@lib/components/savedCanvases/CSavedCanvasesManagementOwnerCell.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CTable from '@lib/components/CTable/CTable.vue'
import CErrorFullScreen from '@lib/components/CError/CErrorFullScreen.vue'
import SmartFoldersConfigurationModal from './SmartFoldersConfigurationModal.vue'
import SmartFoldersMissingConfig from './SmartFoldersMissingConfig.vue'
import useSmartFolders from './SmartFolders.use'

const props = withDefaults(
  defineProps<{
    toolbarHeight: number
    isAnyTypeOfAdmin?: boolean
  }>(),
  {
    isAnyTypeOfAdmin: false,
  },
)

const myUser = inject<ComputedRef<User | null>>(
  'myUser',
  computed(() => null),
)

const pitcherInfo = inject<ComputedRef<{ instanceId: string }>>(
  'pitcherInfo',
  computed(() => ({ instanceId: '' })),
)

const selectedOwner = useLocalStorage<Owner | null>(
  `smart-folders_${pitcherInfo.value.instanceId}_selected_owner`,
  null,
  {
    serializer: { read: (v: string) => JSON.parse(v), write: (v: Owner | null) => JSON.stringify(v) },
  },
)

const emit = defineEmits<{
  open: [e: MouseEvent, canvas: CanvasRetrieve]
}>()

const { t } = useI18n()
const {
  smartFoldersDisplayMode,
  toggleSmartFoldersDisplayMode,
  activeConfiguration,
  isFetchingCanvases,
  isLoadingConfig,
  displayItems,
  breadcrumbItems,
  columns,
  page,
  pageSize,
  pageSizes,
  totalCount,
  onBreadcrumbClick,
  onRowClick,
  fetchData,
  onTableChange,
  loadConfiguration,
} = useSmartFolders(emit)

const showConfigurationModal = ref(false)

function onSmartFoldersConfigure() {
  showConfigurationModal.value = true
}

const isNavigatable = computed(() => smartFoldersDisplayMode.value === 'folders' || breadcrumbItems.value.length > 1)
const shouldDisplayTable = computed(() => !props.isAnyTypeOfAdmin || selectedOwner.value?.value)

async function onOwnerChange(owner: Owner) {
  await fetchData({
    ownerId: owner.value,
    isAnyTypeOfAdmin: props.isAnyTypeOfAdmin,
  })
  selectedOwner.value = owner
}

onMounted(async () => {
  await loadConfiguration()
  if (!props.isAnyTypeOfAdmin) {
    await fetchData()
  }
  if (selectedOwner.value?.value) {
    onOwnerChange(selectedOwner.value)
  }
})

defineExpose({
  showConfigurationModal: onSmartFoldersConfigure,
})
</script>
